const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_X = 48;
const TOP_Y = PAGE_HEIGHT - 54;
const BOTTOM_Y = 46;

const displayLabel = (value) => String(value || "")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value) => {
  if (value == null || value === "") return "Not supplied";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-US", { maximumFractionDigits: 3 });
  if (Array.isArray(value)) return value.map(displayValue).join(", ");
  if (typeof value === "object") return Object.entries(value)
    .map(([key, item]) => `${displayLabel(key)}: ${displayValue(item)}`)
    .join("; ");
  return String(value);
};

const safeText = (value) => String(value || "")
  .replace(/\b(?:claude|gemini)-[a-z0-9.-]+\b/gi, "CrediArc AI")
  .replace(/\b(?:Claude|Anthropic|Gemini|ChatGPT|OpenAI)\b/gi, "CrediArc AI")
  .replace(/[\u2013\u2014]/g, "-")
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201c\u201d]/g, '"')
  .replace(/\u2022/g, "-")
  .replace(/\u2026/g, "...")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^\x20-\x7E\n]/g, "?");

const markdownBlocks = (markdown) => {
  const source = String(markdown || "")
    .replace(/<!--[^]*?-->/g, "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]*\|\|[ \t]*/g, "\n");
  const blocks = [];
  source.split("\n").forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      if (blocks.at(-1)?.type !== "spacer") blocks.push({ type: "spacer" });
      return;
    }
    if (/^\|?\s*:?-{3,}:?(\s*\|\s*:?-{3,}:?)+\s*\|?$/.test(line)) return;
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: heading[1].length <= 2 ? "heading" : "subheading", text: heading[2] });
      return;
    }
    const numberedHeading = line.match(/^(\d{1,2}[a-z]?)[.)]\s+(.+)$/i);
    if (numberedHeading) {
      blocks.push({ type: "heading", text: `${numberedHeading[1]}. ${numberedHeading[2]}` });
      return;
    }
    if (line.startsWith("|")) {
      const cells = line.replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim()).filter(Boolean);
      blocks.push({ type: "table", text: cells.join("  |  ") });
      return;
    }
    const bullet = line.match(/^[-*+]\s+(.+)$/) || line.match(/^\d+[.)]\s+(.+)$/);
    const cleaned = (bullet?.[1] || line)
      .replace(/\[([^\]]+)]\(([^)]+)\)/g, "$1 ($2)")
      .replace(/[*_`>#]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    blocks.push({ type: bullet ? "bullet" : "body", text: cleaned });
  });
  return blocks;
};

const keyValueBlocks = (values = {}) => Object.entries(values)
  .filter(([, value]) => value != null && value !== "" && (!Array.isArray(value) || value.length))
  .map(([key, value]) => ({ type: "keyValue", label: displayLabel(key), text: displayValue(value) }));

export function buildPaymentPlatformReportBlocks(entity = {}) {
  const identity = entity.identity || {};
  const assessment = entity.assessment || {};
  const recommendation = assessment.recommendation || {};
  const applications = (entity.applicationHistory || entity.applications || []).map((application, index) => (
    application && typeof application === "object"
      ? { ...application, id: application.id || application.applicationId || application.externalId || `Application ${index + 1}` }
      : { id: String(application), status: "Recorded" }
  ));
  const blocks = [
    { type: "documentTitle", text: identity.companyName || identity.externalId || identity.website || "Company report" },
    { type: "documentSubtitle", text: `CrediArc company underwriting report | ${new Date().toLocaleString("en-US")}` },
    { type: "heading", text: "Company identity" },
    ...keyValueBlocks({
      companyName: identity.companyName,
      merchantId: identity.externalId || entity.id || entity.entityId,
      registrationNumber: identity.registrationNumber,
      website: identity.website,
      email: identity.email,
      phone: identity.phone,
      address: identity.address,
    }),
    { type: "heading", text: "Assessment and recommendation" },
    ...keyValueBlocks({
      decision: assessment.decision,
      score: assessment.score,
      evidenceCoverage: assessment.coverage == null ? null : `${assessment.coverage}%`,
      riskBand: recommendation.riskBand,
      requestedAmount: entity.requestedAmount ?? entity.signals?.requestedAmount,
      recommendedAmount: recommendation.amount,
      termMonths: recommendation.termMonths,
      factorRate: recommendation.factorRate,
      guardrails: assessment.guardrailHits,
      missingEvidence: assessment.missingFeatures,
    }),
    { type: "heading", text: "Full underwriting report" },
  ];

  if (entity.fullUnderwriting?.reportMarkdown) blocks.push(...markdownBlocks(entity.fullUnderwriting.reportMarkdown));
  else blocks.push({ type: "body", text: "A full underwriting report has not been generated for this company." });

  blocks.push({ type: "heading", text: "Applications and outcomes" });
  blocks.push(...keyValueBlocks({
    applicationCount: entity.applicationCount ?? applications.length,
    funded: entity.outcomes?.funded,
    notFunded: entity.outcomes?.notFunded,
    historicalDecisions: entity.outcomes?.decisions,
  }));
  if (!applications.length) {
    blocks.push({ type: "body", text: "No application history is linked to this company." });
  } else {
    applications.forEach((application) => {
      blocks.push({ type: "subheading", text: application.id });
      blocks.push(...keyValueBlocks({
        status: application.status || application.outcome || application.decision,
        requestedAmount: application.requestedAmount,
        fundedAmount: application.fundedAmount,
        decision: application.decision,
        factorRate: application.factorRate,
        holdbackPercent: application.holdbackPercent,
        submittedAt: application.createdAt || application.submittedAt,
        decidedAt: application.decidedAt,
        fundedAt: application.fundedAt,
        outcome: application.outcome,
      }));
      (application.events || application.history || []).forEach((event) => {
        blocks.push({ type: "bullet", text: [displayLabel(event.type || event.status || "Activity"), event.actor, event.createdAt].filter(Boolean).join(" | ") });
      });
    });
  }

  if (Array.isArray(entity.performanceHistory) && entity.performanceHistory.length) {
    blocks.push({ type: "heading", text: "Performance history" });
    entity.performanceHistory.forEach((record, index) => {
      blocks.push({ type: "subheading", text: record.period || record.month || record.date || `Period ${index + 1}` });
      blocks.push(...keyValueBlocks(record));
    });
  }
  return blocks;
}

const splitLongWord = (word, font, size, maxWidth) => {
  const chunks = [];
  let current = "";
  for (const character of word) {
    if (current && font.widthOfTextAtSize(current + character, size) > maxWidth) {
      chunks.push(current);
      current = character;
    } else current += character;
  }
  if (current) chunks.push(current);
  return chunks;
};

const wrapText = (text, font, size, maxWidth) => {
  const words = safeText(text).split(/\s+/).filter(Boolean).flatMap((word) => (
    font.widthOfTextAtSize(word, size) > maxWidth ? splitLongWord(word, font, size, maxWidth) : [word]
  ));
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (current && font.widthOfTextAtSize(candidate, size) > maxWidth) {
      lines.push(current);
      current = word;
    } else current = candidate;
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
};

export async function createPaymentPlatformReportPdf(entity) {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  document.setTitle(`${entity?.identity?.companyName || "Company"} underwriting report`);
  document.setAuthor("CrediArc");
  document.setCreator("CrediArc");
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  let page;
  let y;

  const addPage = () => {
    page = document.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    y = TOP_Y;
    page.drawText("CrediArc", { x: MARGIN_X, y: PAGE_HEIGHT - 31, size: 11, font: bold, color: rgb(0.04, 0.16, 0.29) });
    page.drawLine({ start: { x: MARGIN_X, y: PAGE_HEIGHT - 39 }, end: { x: PAGE_WIDTH - MARGIN_X, y: PAGE_HEIGHT - 39 }, thickness: 0.7, color: rgb(0.86, 0.9, 0.95) });
  };
  const ensureSpace = (height) => { if (!page || y - height < BOTTOM_Y) addPage(); };
  const drawWrapped = (text, { size = 9.5, font = regular, color = rgb(0.16, 0.27, 0.37), indent = 0, lineHeight = size * 1.45, before = 0, after = 4 } = {}) => {
    const maxWidth = PAGE_WIDTH - (MARGIN_X * 2) - indent;
    const lines = wrapText(text, font, size, maxWidth);
    ensureSpace(before + (lines.length * lineHeight) + after);
    y -= before;
    lines.forEach((line) => {
      if (y - lineHeight < BOTTOM_Y) addPage();
      page.drawText(line, { x: MARGIN_X + indent, y, size, font, color });
      y -= lineHeight;
    });
    y -= after;
  };

  addPage();
  for (const block of buildPaymentPlatformReportBlocks(entity)) {
    if (block.type === "spacer") { y -= 4; continue; }
    if (block.type === "documentTitle") {
      drawWrapped(block.text, { size: 22, font: bold, color: rgb(0.04, 0.16, 0.29), before: 9, after: 5, lineHeight: 27 });
    } else if (block.type === "documentSubtitle") {
      drawWrapped(block.text, { size: 9.5, color: rgb(0.39, 0.47, 0.57), after: 15 });
    } else if (block.type === "heading") {
      ensureSpace(36);
      y -= 12;
      page.drawLine({ start: { x: MARGIN_X, y: y + 5 }, end: { x: PAGE_WIDTH - MARGIN_X, y: y + 5 }, thickness: 0.7, color: rgb(0.86, 0.9, 0.95) });
      drawWrapped(block.text, { size: 14, font: bold, color: rgb(0.04, 0.16, 0.29), after: 7, lineHeight: 18 });
    } else if (block.type === "subheading") {
      drawWrapped(block.text, { size: 11, font: bold, color: rgb(0.09, 0.31, 0.53), before: 6, after: 4 });
    } else if (block.type === "keyValue") {
      drawWrapped(`${block.label}: ${block.text}`, { size: 9.3, after: 2 });
    } else if (block.type === "bullet") {
      drawWrapped(`- ${block.text}`, { size: 9.3, indent: 10, after: 2 });
    } else if (block.type === "table") {
      drawWrapped(block.text, { size: 8.2, font: regular, color: rgb(0.18, 0.27, 0.38), indent: 5, after: 2 });
    } else {
      drawWrapped(block.text, { size: 9.3, after: 4 });
    }
  }

  const pages = document.getPages();
  pages.forEach((pdfPage, index) => {
    const footer = `CrediArc | Company underwriting report | Page ${index + 1} of ${pages.length}`;
    pdfPage.drawText(footer, { x: MARGIN_X, y: 24, size: 7.5, font: regular, color: rgb(0.48, 0.54, 0.63) });
  });
  return document.save();
}

export async function downloadPaymentPlatformReport(entity) {
  const bytes = await createPaymentPlatformReportPdf(entity);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const name = safeText(entity?.identity?.companyName || entity?.identity?.externalId || "Company")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "") || "Company";
  anchor.href = url;
  anchor.download = `${name}-Underwriting-Report.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
